import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import { ButtonWrapper, SearchInputWrapper, StyledButton } from './styled';
import DropdownSelect from '../../components/DropdownSelect';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import CustomDatePicker from '../../components/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { SnackbarSeverityType } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import AttendanceReportTable from './components/AttendanceReportTable';
import {
  fetchAttendanceTrackingReportData,
  transformAttendanceTrackingReportData
} from './utils/transformAttendanceReportData';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { downloadReport } from '../../utils/reportUtils';
import moment from 'moment';
import { PaperWrapper, Title } from '../common/styled';

const AttendanceReportPage = () => {
  const [grade, setGrade] = useState<string>('');
  const [division, setDivision] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [attendanceReportData, setAttendanceReportData] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const { gradeOptions, divisionOptions } = useSchoolClassesConfigHook();

  const DATE_FORMAT = 'YYYY-MM-DD';

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const fromDate = startDate
        ? moment(startDate.toDate()).format(DATE_FORMAT)
        : '';
      const toDate = endDate
        ? moment(endDate.toDate()).format(DATE_FORMAT)
        : '';

      const response = await fetchAttendanceTrackingReportData({
        school: loggedInUser.school,
        grade,
        division,
        startDate: fromDate,
        endDate: toDate
      });
      const transformedAttendanceReportData =
        transformAttendanceTrackingReportData(response, false);
      setAttendanceReportData(transformedAttendanceReportData);
      showToast(
        'Successfully fetched attendance tracking report',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message ||
          'Error fetching attendance tracking report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const fromDate = startDate
        ? moment(startDate.toDate()).format(DATE_FORMAT)
        : '';
      const toDate = endDate
        ? moment(endDate.toDate()).format(DATE_FORMAT)
        : '';

      const response = await fetchAttendanceTrackingReportData({
        school: loggedInUser.school,
        grade,
        division,
        startDate: fromDate,
        endDate: toDate
      });
      const transformedAttendanceReportData =
        transformAttendanceTrackingReportData(response, true);
      downloadReport(
        transformedAttendanceReportData,
        'Attendance Tracking Report',
        'Attendance_Tracking_Report',
        showToast
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message ||
          'Error downloading attendance tracking report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastOpen(true);
    setToastMessage(message);
    setToastSeverity(severity);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const isFormIncomplete = !grade || !division || !startDate || !endDate;

  const isViewButtonDisabled = isSearching || isFormIncomplete;
  const isDownloadButtonDisabled =
    isDownloading || isSearching || isFormIncomplete;

  return (
    <>
      <Title>Attendance Tracking Report</Title>
      <PaperWrapper sx={{ minHeight: 'auto' }}>
        <SearchInputWrapper>
          <DropdownSelect
            label='Grade'
            name='grade'
            options={gradeOptions}
            value={grade}
            required
            onChange={(event) => {
              setGrade(event.target.value as string);
              setHasSearched(false);
            }}
            placeholder='Select Grade'
            hasTopLabel
          />
          <DropdownSelect
            label='Division'
            name='division'
            value={division}
            options={divisionOptions}
            required
            onChange={(event) => {
              setDivision(event.target.value as string);
              setHasSearched(false);
            }}
            placeholder='Select Division'
            hasTopLabel
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CustomDatePicker
              label='From Date'
              required
              value={startDate}
              onChange={(newValue: any) => {
                setStartDate(newValue);
                setHasSearched(false);
              }}
            />
            <CustomDatePicker
              label='To Date'
              required
              value={endDate}
              onChange={(newValue: any) => {
                setEndDate(newValue);
                setHasSearched(false);
              }}
            />
          </LocalizationProvider>
        </SearchInputWrapper>
        <ButtonWrapper>
          <StyledButton onClick={handleSearch} disabled={isViewButtonDisabled}>
            {isSearching ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'View Report'
            )}
          </StyledButton>

          <StyledButton
            onClick={handleDownload}
            disabled={isDownloadButtonDisabled}
          >
            {isDownloading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Download Report'
            )}
          </StyledButton>
        </ButtonWrapper>
      </PaperWrapper>
      {hasSearched && (
        <AttendanceReportTable
          attendanceReportTableData={attendanceReportData}
        />
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default AttendanceReportPage;
